/**
 * Typed REST request wrapper for the shared API layer (issue #1963, Phase 1).
 *
 * Thin wrapper over the global `axios` instance so the existing interceptors
 * (`X-Request-Id` / `X-Session-Id`, issues #1446/#1447) still apply. It does
 * two things the raw client doesn't: returns the typed `response.data`
 * directly (no `.data` unwrapping at every call site) and normalizes failures
 * into an {@link ApiError}. It deliberately does NOT swallow errors or surface
 * UI — surfacing is the shared handler's job (`handleApiError`). Retries are
 * off by default; opt in per call via the `retry` option (issue #2096).
 *
 * Services should call `api.get<T>(...)` etc. instead of importing `axios`
 * directly (the Phase 0 convention in frontend/CLAUDE.md).
 */

import axios, { type AxiosRequestConfig } from "axios";

import { toApiError } from "./ApiError";
import { isMaintenanceResponse, markMaintenanceActive } from "./maintenanceState";
import { type RetryConfig, resolveRetryConfig, withRetry } from "./retry";

export interface ApiRequestOptions {
  params?: AxiosRequestConfig["params"];
  headers?: AxiosRequestConfig["headers"];
  /** Tie the request to a lifecycle so it can be aborted (see requestAbort.ts). */
  signal?: AbortSignal;
  responseType?: AxiosRequestConfig["responseType"];
  timeout?: number;
  /** Upload progress callback (pass-through to axios) — e.g. file-upload progress bars. */
  onUploadProgress?: AxiosRequestConfig["onUploadProgress"];
  /** Message used when neither the server nor the error provides one. */
  fallbackMessage?: string;
  /**
   * Opt in to exponential-backoff retries (off by default). Overrides merge
   * onto the shared {@link DEFAULT_RETRY_CONFIG} — e.g. `{ maxRetries: 1 }`.
   * Uses the shared {@link withRetry} primitive so backoff/policy match chat's
   * retrying client.
   */
  retry?: Partial<RetryConfig>;
}

type RequestConfig = AxiosRequestConfig & {
  fallbackMessage?: string;
  retry?: Partial<RetryConfig>;
};

async function request<T>(config: RequestConfig): Promise<T> {
  const { fallbackMessage, retry, ...axiosConfig } = config;
  try {
    const send = () => axios.request<T>(axiosConfig);
    const response = retry ? await withRetry(send, resolveRetryConfig(retry)) : await send();
    return response.data;
  } catch (error) {
    const apiError = toApiError(error, fallbackMessage);
    // Latch the maintenance overlay when any call hits the backend's 503
    // maintenance body (#2343), so a blocked write / login surfaces "back soon".
    if (isMaintenanceResponse(apiError.status, apiError.data)) {
      markMaintenanceActive();
    }
    throw apiError;
  }
}

function toConfig(options?: ApiRequestOptions): RequestConfig {
  if (!options) {
    return {};
  }
  const { fallbackMessage, ...rest } = options;
  return { ...rest, fallbackMessage };
}

export const api = {
  request,

  get<T>(url: string, options?: ApiRequestOptions): Promise<T> {
    return request<T>({ ...toConfig(options), method: "get", url });
  },

  delete<T>(url: string, options?: ApiRequestOptions): Promise<T> {
    return request<T>({ ...toConfig(options), method: "delete", url });
  },

  post<T>(url: string, data?: unknown, options?: ApiRequestOptions): Promise<T> {
    return request<T>({ ...toConfig(options), method: "post", url, data });
  },

  put<T>(url: string, data?: unknown, options?: ApiRequestOptions): Promise<T> {
    return request<T>({ ...toConfig(options), method: "put", url, data });
  },

  patch<T>(url: string, data?: unknown, options?: ApiRequestOptions): Promise<T> {
    return request<T>({ ...toConfig(options), method: "patch", url, data });
  },
};
